// Voice calling overview: how many calls, how many connected, what failed, and the boxes.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Clock,
  Hourglass,
  PhoneCall,
  PhoneForwarded,
  RefreshCw,
  Timer,
  Radio,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useVoiceOverviewQuery, useVoiceRefresh } from '@/client/api/voice/voice.query';
import { Card, PageShell, StatCard, Tabs, fmtNum } from '../Cloud/cloudUi';
import {
  AccessDenied,
  BoxStatusPill,
  ErrorNotice,
  FetchingChip,
  LoadingNote,
  OnlineDot,
  VOICE_BASE,
  fmtTalk,
  freeModems,
  titleCase,
  useVoicePerms,
} from './voiceUi';

const WINDOW_LABELS = ['7 days', '30 days', '90 days'] as const;
const DAYS: Record<(typeof WINDOW_LABELS)[number], number> = { '7 days': 7, '30 days': 30, '90 days': 90 };

const AXIS = { fontSize: 11, fill: '#9ca3af' };
const TIP = { fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' };

const DayChart: React.FC<{
  data: Record<string, number | string>[];
  bars: { key: string; color: string; name: string }[];
  stacked?: boolean;
  empty: boolean;
  emptyText: string;
}> = ({ data, bars, stacked, empty, emptyText }) =>
  empty ? (
    <p className="py-10 text-center text-sm text-gray-400">{emptyText}</p>
  ) : (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="15%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" tick={AXIS} />
          <YAxis allowDecimals={false} width={32} tickLine={false} axisLine={false} tick={AXIS} />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.03)' }} contentStyle={TIP} />
          {bars.map((b) => (
            <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} stackId={stacked ? 's' : undefined} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

const VoiceOverviewPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Voice calling', href: VOICE_BASE }, { label: 'Overview' }]);
  const perms = useVoicePerms();
  const [win, setWin] = useState<(typeof WINDOW_LABELS)[number]>('30 days');
  const days = DAYS[win];
  const q = useVoiceOverviewQuery(days);
  const refresh = useVoiceRefresh();
  const d = q.data;

  if (perms.loading || !perms.canView) {
    return (
      <PageShell title="Voice calling" description="">
        {perms.loading ? <Skeleton className="h-64 w-full" /> : <AccessDenied />}
      </PageShell>
    );
  }

  const t = d?.totals;
  const series = (d?.series ?? []).map((p) => ({
    ...p,
    label: moment(p.date).format('D MMM'),
    // Connected calls that ended some other way are not a separate bucket: chart what each call became.
    other: Math.max(0, p.calls - p.connected - p.failed - p.expired),
    minutes: Math.round((p.talk_seconds / 60) * 10) / 10,
  }));
  const maxReason = Math.max(1, ...(d?.reasons ?? []).map((r) => r.count));

  return (
    <PageShell
      title="Voice calling"
      description="Private calls between buyers and sellers, placed through the call boxes."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <FetchingChip />
          <Tabs value={win} onChange={setWin} options={WINDOW_LABELS} />
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading || !d || !t ? (
        <>
          <LoadingNote>Loading call figures for the last {days} days.</LoadingNote>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-56 w-full" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
            <StatCard label="Calls" value={fmtNum(t.calls)} hint={`last ${days} days`} icon={<PhoneCall className="h-5 w-5 text-blue-700" />} color="bg-blue-50" />
            <StatCard
              label="Connect rate"
              value={t.connect_rate == null ? '—' : `${Math.round(t.connect_rate * 100)}%`}
              hint={`${fmtNum(t.connected)} reached the seller`}
              icon={<PhoneForwarded className="h-5 w-5 text-green-700" />}
              color="bg-green-50"
            />
            <StatCard label="Avg talk time" value={fmtTalk(t.avg_talk_seconds)} hint={`${fmtNum(Math.round(t.talk_seconds / 60))} min in total`} icon={<Timer className="h-5 w-5 text-violet-700" />} color="bg-violet-50" />
            <StatCard label="Live now" value={fmtNum(d.live)} hint="in progress" icon={<Radio className="h-5 w-5 text-sky-700" />} color="bg-sky-50" />
            <StatCard label="Failed" value={fmtNum(t.failed)} hint={`${fmtNum(t.caller_rejected)} rejected by caller`} icon={<AlertTriangle className="h-5 w-5 text-red-700" />} color="bg-red-50" />
            <StatCard label="Expired" value={fmtNum(t.expired)} hint="seller never picked up in time" icon={<Hourglass className="h-5 w-5 text-amber-700" />} color="bg-amber-50" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Calls per day" note="connected, failed, expired">
              <DayChart
                data={series}
                stacked
                empty={!series.some((s) => s.calls > 0)}
                emptyText="No calls in this window."
                bars={[
                  { key: 'connected', color: '#22c55e', name: 'Connected' },
                  { key: 'failed', color: '#ef4444', name: 'Failed' },
                  { key: 'expired', color: '#f59e0b', name: 'Expired' },
                  { key: 'other', color: '#cbd5e1', name: 'Other' },
                ]}
              />
            </Card>
            <Card title="Talk minutes per day">
              <DayChart
                data={series}
                empty={!series.some((s) => s.talk_seconds > 0)}
                emptyText="Nobody talked in this window."
                bars={[{ key: 'minutes', color: '#7c3aed', name: 'Minutes' }]}
              />
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Why calls failed or expired" flush>
              {d.reasons.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-gray-400">No failures in this window.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {d.reasons.map((r) => (
                    <li key={r.reason} className="px-4 py-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-800">{titleCase(r.reason)}</span>
                        <span className="text-gray-600">{fmtNum(r.count)}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-red-400" style={{ width: `${(r.count / maxReason) * 100}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card
              title="Ad plays"
              flush
              action={
                <Link to={`${VOICE_BASE}/announcements`} className="text-xs text-blue-600 hover:underline">
                  Manage ads
                </Link>
              }
            >
              {d.ads.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-gray-400">No ad was played in this window.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {d.ads.map((a) => (
                    <li key={a.prompt_id} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="text-gray-800">{a.name}</span>
                      <span className="text-gray-600">{fmtNum(a.plays)} plays</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <Card
            title="Boxes"
            action={
              <Link to={`${VOICE_BASE}/boxes`} className="text-xs text-blue-600 hover:underline">
                All boxes
              </Link>
            }
          >
            {d.boxes.length === 0 ? (
              <p className="text-center text-sm text-gray-400">
                No box has reported in yet. A new box shows up here, waiting for approval, as soon as it starts.
              </p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {d.boxes.map((b) => (
                  <Link
                    key={b.box_id}
                    to={`${VOICE_BASE}/boxes/${encodeURIComponent(b.box_id)}`}
                    className="rounded-xl border border-gray-100 p-3 hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-gray-900">{b.name || b.box_id}</p>
                        <p className="truncate font-mono text-xs text-gray-500">{b.box_id}</p>
                      </div>
                      <BoxStatusPill status={b.status} />
                    </div>
                    <div className="mt-2 space-y-1 text-xs text-gray-600">
                      <OnlineDot online={b.online} lastSeen={b.last_seen_at} />
                      <p className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3 text-gray-400" />
                        {freeModems(b)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </PageShell>
  );
};

export default VoiceOverviewPage;
